import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { css } from '@emotion/core';
import { theme } from '@weave/theme-original';
import {
  Alert,
  Heading,
  PrimaryButton,
  SpinningLoader,
  SyncIcon,
  Text,
} from '@weave/design-system';
import { UnifyLocationsTable } from '../unify-locations-table/unify-locations-table';
import { UnifyHandoffFooter } from '../unify-handoff-footer/unify-handoff-footer';
import { useUnifyHandoffContext } from '../../unify-handoff-context';
import { useUnifyHandoffMetricsEvaluation } from '../../hooks/use-unify-handoff-metrics-evaluation';
import { MetricAccordion } from '../../../handoff-checklist-v2/components/metric-accordion/metric-accordion';
import { MetricListItem } from '../../../handoff-checklist-v2/components/metric-list-item/metric-list-item';
import { MetricDetailModal } from '../../../handoff-checklist-v2/components/metric-detail-modal/metric-detail-modal';
import { ExceptionChangeParams } from '../../../handoff-checklist-v2/components/metric-accordion/multi-location-metric-table';
import { MetricItemData } from '../../../handoff-checklist-v2/handoff-checklist-v2.types';
import { UnifyEvalTab, UnifyMetricsTabs } from './unify-metrics-tabs';
import {
  applyExceptionsToMetrics,
  buildResultsByLocation,
  canSubmitMetrics,
  groupMetricsBySection,
  splitMetricsByAttention,
} from './unify-evaluation.helpers';

export const UnifyEvaluation: React.FC = () => {
  const {
    selectedLocations,
    locationDetailsMap,
    showValidationErrors,
    handleNotesChange,
    handlePocChange,
    isSaving,
    isSubmitting,
    onSubmit: onSubmitContext,
  } = useUnifyHandoffContext();

  const [activeTab, setActiveTab] = useState<UnifyEvalTab>('needs-attention');
  const [exceptions, setExceptions] = useState<Record<string, string>>({});
  const [selectedCompletedMetric, setSelectedCompletedMetric] =
    useState<MetricItemData | null>(null);

  const {
    data: rawMetrics,
    isLoading: isEvaluating,
    error: evaluationError,
    refetch,
  } = useUnifyHandoffMetricsEvaluation({
    locations: selectedLocations,
    enabled: selectedLocations.length > 0,
  });

  const handleExceptionChange = useCallback(
    ({ metricId, locationId, criteriaIndex, exception }: ExceptionChangeParams) => {
      const key = `${locationId}:${metricId}:${criteriaIndex}`;
      setExceptions((prev) => ({
        ...prev,
        [key]: exception,
      }));
    },
    []
  );

  const resolvedMetrics = useMemo(() => {
    return applyExceptionsToMetrics(rawMetrics, exceptions);
  }, [rawMetrics, exceptions]);

  // Final submittable metric results partitioned per location
  const resultsByLocation = useMemo(() => {
    return buildResultsByLocation(resolvedMetrics, selectedLocations);
  }, [resolvedMetrics, selectedLocations]);

  const { processing, needsAttention, completed } = useMemo(() => {
    return splitMetricsByAttention(resolvedMetrics);
  }, [resolvedMetrics]);

  const groupedNeedsAttention = useMemo(() => {
    return groupMetricsBySection(needsAttention);
  }, [needsAttention]);

  const groupedCompleted = useMemo(() => {
    return groupMetricsBySection(completed);
  }, [completed]);

  const groupedProcessing = useMemo(() => {
    return groupMetricsBySection(processing);
  }, [processing]);

  useEffect(() => {
    const activeTabIsVisible =
      (activeTab === 'needs-attention' && needsAttention.length > 0) ||
      (activeTab === 'processing' && processing.length > 0) ||
      activeTab === 'completed';

    if (!activeTabIsVisible) {
      setActiveTab(
        needsAttention.length > 0
          ? 'needs-attention'
          : processing.length > 0
          ? 'processing'
          : 'completed'
      );
    }
  }, [activeTab, needsAttention.length, processing.length]);

  const canSubmit = useMemo(() => {
    if (evaluationError) {
      return false;
    }
    return canSubmitMetrics(resolvedMetrics, isEvaluating, selectedLocations.length);
  }, [resolvedMetrics, isEvaluating, selectedLocations.length, evaluationError]);

  const hasExceptions = useMemo(() => {
    return Object.values(exceptions).some(
      (val) => typeof val === 'string' && val.trim().length > 0
    );
  }, [exceptions]);

  const handleSubmit = useCallback(() => {
    onSubmitContext(resultsByLocation);
  }, [onSubmitContext, resultsByLocation]);

  return (
    <div css={styles.container}>
      {/* Location Overview Section */}
      <section css={styles.section}>
        <Heading level={2} css={styles.sectionHeading}>
          Location Overview
        </Heading>
        <UnifyLocationsTable
          locations={selectedLocations}
          mode="review"
          selectedLocationIds={selectedLocations.map((loc) => loc.locationId)}
          locationDetailsMap={locationDetailsMap}
          onNotesChange={handleNotesChange}
          onPocChange={handlePocChange}
          showValidationErrors={showValidationErrors}
        />
      </section>

      {/* Onboarding Handoff Checklist Section */}
      <section css={styles.section}>
        <div css={styles.checklistHeader}>
          <div css={styles.checklistTitleContainer}>
            <Heading level={2} css={styles.sectionHeading}>
              Onboarding Handoff Checklist
            </Heading>
            <Text color="light" size="small" css={styles.description}>
              Ensure all accounts are ready for handoff by viewing the checklists below
            </Text>
          </div>
          <PrimaryButton
            onClick={refetch}
            disabled={isEvaluating || isSaving || isSubmitting}
            css={styles.refreshButton}
          >
            <SyncIcon css={styles.refreshIcon} />
            Refresh
          </PrimaryButton>
        </div>

        {evaluationError && <Alert type="error">{evaluationError}</Alert>}

        {/* Tab Filters: Needs Attention / Completed */}
        <UnifyMetricsTabs
          activeTab={activeTab}
          onChange={setActiveTab}
          needsAttentionCount={needsAttention.length}
          processingCount={processing.length}
        />

        {/* Checklist Metric Content */}
        {isEvaluating && rawMetrics.length === 0 ? (
          <div css={styles.loadingContainer}>
            <SpinningLoader size="medium" />
            <Text color="light" size="medium">
              Evaluating checks across all {selectedLocations.length} locations...
            </Text>
          </div>
        ) : activeTab === 'needs-attention' ? (
          <div css={styles.tabContentList}>
            {groupedNeedsAttention.length === 0 ? (
              <div css={styles.emptyTabContainer}>
                <Text color="light" size="medium">
                  No checks require attention.
                </Text>
              </div>
            ) : (
              groupedNeedsAttention.map((group) => (
                <div key={group.sectionName} css={styles.sectionGroup}>
                  <Heading level={3} css={styles.sectionTitle}>
                    {group.sectionName}
                  </Heading>
                  <div css={styles.metricList}>
                    {group.metrics.map((metric) => (
                      <MetricAccordion
                        key={metric.id}
                        metric={metric}
                        showWhereToFix={true}
                        readOnly={false}
                        onLocationExceptionChange={handleExceptionChange}
                      />
                    ))}
                  </div>
                </div>
              ))
            )}
          </div>
        ) : activeTab === 'processing' ? (
          <div css={styles.tabContentList}>
            {groupedProcessing.map((group) => (
              <div key={group.sectionName} css={styles.sectionGroup}>
                <Heading level={3} css={styles.sectionTitle}>
                  {group.sectionName}
                </Heading>
                <div css={styles.metricList}>
                  {group.metrics.map((metric) => (
                    <MetricListItem
                      key={metric.id}
                      metric={metric}
                      onOpenMetricDetail={() => setSelectedCompletedMetric(metric)}
                      readOnly={true}
                      showWhereToFix={false}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div css={styles.tabContentList}>
            {groupedCompleted.length === 0 ? (
              <div css={styles.emptyTabContainer}>
                <Text color="light" size="medium">
                  No completed checks yet.
                </Text>
              </div>
            ) : (
              groupedCompleted.map((group) => (
                <div key={group.sectionName} css={styles.completedSectionGroup}>
                  <Heading level={3} css={styles.sectionTitle}>
                    {group.sectionName}
                  </Heading>
                  <div css={styles.completedList}>
                    {group.metrics.map((metric) => {
                      return (
                        <MetricListItem
                          key={metric.id}
                          metric={metric}
                          onOpenMetricDetail={() => setSelectedCompletedMetric(metric)}
                          readOnly={false}
                          showWhereToFix={false}
                        />
                      );
                    })}
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </section>

      {/* Completed Metric Detail Modal */}
      <MetricDetailModal
        metric={selectedCompletedMetric}
        isOpen={selectedCompletedMetric !== null}
        onClose={() => setSelectedCompletedMetric(null)}
      />

      {/* Shared Footer Navigation */}
      <UnifyHandoffFooter
        step="evaluation"
        canSubmit={canSubmit}
        onSubmit={handleSubmit}
        hasExceptions={hasExceptions}
      />
    </div>
  );
};

const styles = {
  container: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(3)};
  `,
  section: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(1.5)};
  `,
  sectionHeading: css`
    margin: 0;
    font-size: 20px;
    font-weight: 700;
  `,
  description: css`
    margin: 0;
  `,
  checklistHeader: css`
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
  `,
  checklistTitleContainer: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(0.5)};
    flex: 1;
  `,
  refreshButton: css`
    && {
      width: fit-content;
      display: inline-flex;
      gap: ${theme.spacing(0.5)};
    }
  `,
  refreshContent: css`
    align-items: center;
  `,
  refreshIcon: css`
    && {
      width: 16px;
      height: 16px;
      margin: 0;
    }
  `,
  tabContentList: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(2)};
  `,
  sectionGroup: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(1.5)};
  `,
  completedSectionGroup: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(1)};
    padding-bottom: ${theme.spacing(2)};
    border-bottom: 1px solid #e8e8e8;

    &:last-of-type {
      border-bottom: none;
    }
  `,
  sectionTitle: css`
    margin: 0;
    font-size: 16px;
    font-weight: 700;
    color: #1f1f1f;
  `,
  completedList: css`
    display: flex;
    flex-direction: column;
  `,
  metricList: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(1)};
  `,
  loadingContainer: css`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: ${theme.spacing(2)};
    padding: ${theme.spacing(6)};
  `,
  emptyTabContainer: css`
    padding: ${theme.spacing(4)};
    text-align: center;
  `,
};
